import CheckBox from "@mui/icons-material/CheckBox";
import IndeterminateCheckBox from "@mui/icons-material/IndeterminateCheckBox";
import {
	Box, Checkbox, Chip,
	DialogActions, DialogContent, DialogContentText,
	DialogTitle, Stack
} from "@mui/material";
import { useState } from "react";

import messages from "../../../../constants/messages";
import {
	CoAppActionButton, CoAppCloseIcon, CoAppDialog,
	CoAppDialogTooltipContainer, CoAppDialogTooltipText, CoAppLightTooltip,
	CoAppTextButton
} from "../../styled/global.styled";
import CoAppMultiSelect from "../inputs/CoAppMultiSelect";

export default function CoAppBulkApplyModal(props) {
	const {
		dialogOpen, dialogTitle, cancelClickHandler,
		confirmClickhandler, availableItems, items,
		selectedItemChangeHandler, resource, targetResource,
		selectedRules, totalRules, itemsRemoved,
		calculateItemsRemovedFromAll, calculateItemsToAddToAll, calculateUnchangedItems
	} = props;

	const model = resource.toLowerCase().includes("tag") ? "tags" : "groups";
	const [isConfirmationStep, setIsConfirmationStep] = useState(false);

	const handleClickNext = () => {
		setIsConfirmationStep(true);
	};

	const handleClickBack = () => {
		setIsConfirmationStep(false);
	};

	const getChipBackground = (tagType) => {
		switch (tagType) {
			case "added":
				return "#00000014";
			case "removed":
				return "#DE37304D";
			case "unchanged":
				return "repeating-linear-gradient(45deg, #FFFFFF 0px, #FFFFFF 5px, #EDEFEE 5px, #EDEFEE 15px, #FFFFFF 15px, #FFFFFF 15px, #EDEFEE 15px, #EDEFEE 20px)";
			case "mixedState":
				return "repeating-linear-gradient(45deg, #FFFFFF 0px, #FFFFFF 5px, #EDEFEE 5px, #EDEFEE 15px, #FFFFFF 15px, #FFFFFF 15px, #EDEFEE 15px, #EDEFEE 20px)";
			default:
				return "#00000014";
		}
	};

	const renderReviewSection = (label, names, tagType) => (
		<Box key={label}>
			<DialogContentText variant="caption" sx={{ color: "text.secondary", fontWeight: 600, display: "block", mb: 0.75 }}>
				{label}
			</DialogContentText>
			{names.length ? (
				<Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.75 }}>
					{names.map((name) => (
						<Chip key={name} label={name} sx={{ background: getChipBackground(tagType) }} />
					))}
				</Stack>
			) : (
				<DialogContentText variant="body2" sx={{ color: "text.disabled" }}>None</DialogContentText>
			)}
		</Box>
	);

	const getTagType = (option) => {
		const couldBeMixedState = selectedRules.some(ruleId => {
			const foundRule = totalRules.find(rule => rule.id === ruleId);
			return foundRule[model].map(item => item.name).includes(option);
		});
		let isAppliedToAll = false;
		if (couldBeMixedState) {
			isAppliedToAll = selectedRules.every(ruleId => {
				const foundRule = totalRules.find(rule => rule.id === ruleId);
				return foundRule[model].map(item => item.name).includes(option) || itemsRemoved.map(item => item.name).includes(option);
			});
		}

		let isCurrentlyAppliedToNone = false;
		if (!couldBeMixedState) {
			isCurrentlyAppliedToNone = selectedRules.every(ruleId => {
				const foundRule = totalRules.find(rule => rule.id === ruleId);
				return !foundRule[model].map(item => item.name).includes(option);
			});
		}

		if (isAppliedToAll || isCurrentlyAppliedToNone) {
			return "added";
		} else if (couldBeMixedState) {
			return "mixedState";
		} else {
			return "removed";
		}
	};

	const calculateAppliedToCount = (tagType, option) => {
		let count = 0;
		let appliedToAll = false;
		if (tagType === "mixedState" || tagType === "unchanged") {
			count = selectedRules.filter(ruleId => {
				const foundRule = totalRules.find(rule => rule.id === ruleId);
				return foundRule[model].map(item => item.name).includes(option);
			}).length;
		} else {
			count = selectedRules.length;
			appliedToAll = true;
		}
		return { count, appliedToAll };
	};

	const handleRenderTagsWhileEditing = (tagValue, getItemProps) => {
		return tagValue.map((option, index) => {
			const { key, ...tagProps } = getItemProps({ index });
			const tagType = getTagType(option);
			const { count, appliedToAll } = calculateAppliedToCount(tagType, option);
			return (
				<CoAppLightTooltip
					key={key}
					title={
						<CoAppDialogTooltipContainer>
							<CoAppDialogTooltipText name="true">{`Applied to ${appliedToAll ? `all ${count}` : `${count}`} of the selected ${targetResource}`}</CoAppDialogTooltipText>
						</CoAppDialogTooltipContainer>
					}
				>
					<Chip {...tagProps} label={option} sx={{ background: getChipBackground(tagType) }} />
				</CoAppLightTooltip>
			);
		});
	};

	const handleRenderCheckboxes = (props, option, { selected }) => {
		const { key, ...optionProps } = props;
		const tagType = getTagType(option);
		return (
			<li key={key ?? option.id ?? option.name ?? option} {...optionProps} style={{ cursor: isConfirmationStep ? "default" : "pointer", pointerEvents: isConfirmationStep ? "none" : "auto" }}>
				<Checkbox
					style={{ marginRight: 8 }}
					checked={selected}
					value={option}
					checkedIcon={tagType === "mixedState" ? <IndeterminateCheckBox /> : <CheckBox />}
				/>
				<div style={{ display: "block" }}>
					{option}
				</div>
			</li >
		);
	};

	const resetModal = () => {
		setIsConfirmationStep(false);
		cancelClickHandler();
	};

	const handleConfirmClick = () => {
		confirmClickhandler();
		setIsConfirmationStep(false);
	};

	return (
		<CoAppDialog
			open={dialogOpen}
			onClose={resetModal}
			slotProps={{
				paper: {
					sx: {
						borderRadius: "16px",
						padding: 0,
						width: "520px"
					}
				}
			}}		
		>
			<DialogTitle variant="h6" sx={{ px: 3, pt: 3, pb: 1, pr: 6, fontWeight: 700 }}>{isConfirmationStep ? "Save changes?" : dialogTitle}</DialogTitle>
			<CoAppCloseIcon onClick={resetModal} top={20} right={20} />
			<DialogContent sx={{ px: 3, py: 0 }}>
				{
					isConfirmationStep ?
						<Stack spacing={2.5} sx={{ mt: 1 }}>
							{renderReviewSection(`${resource} added to all selected ${targetResource} (${calculateItemsToAddToAll().length})`, calculateItemsToAddToAll().map((item) => item.name), "added")}
							{renderReviewSection(`${resource} removed from all selected ${targetResource} (${calculateItemsRemovedFromAll().length})`, calculateItemsRemovedFromAll().map((item) => item.name), "removed")}
							{renderReviewSection(`${resource} left unchanged (${calculateUnchangedItems().length})`, calculateUnchangedItems().map((item) => item.name), "unchanged")}
						</Stack>
						:
						<>
							<DialogContentText
								variant="body2"
								color="text.primary"
								sx={{ lineHeight: 1.6, mb: 3 }}
							>
								{messages.BULK_APPLY_MODAL_MESSAGE(resource.replaceAll("(", "").replaceAll(")", ""), targetResource)}
							</DialogContentText>
							<CoAppMultiSelect
								disabled={false}
								isFreeSolo={false}
								label={resource}
								handleRenderTags={(tagValue, getItemProps) => handleRenderTagsWhileEditing(tagValue, getItemProps)}
								options={availableItems.map((item) => item.name)}
								value={items.map((item) => item.name)}
								onChange={(e, value, reason) => selectedItemChangeHandler(value)}
								hasToolTip={true}
								customRenderTags={handleRenderTagsWhileEditing}
								customRenderCheckboxes={handleRenderCheckboxes}
							/>
						</>
				}
			</DialogContent>
			<DialogActions sx={{ px: 3, pt: 1.5, pb: 3, gap: 1 }}>
				<CoAppTextButton onClick={isConfirmationStep ? handleClickBack : resetModal}>{isConfirmationStep ? "Back" : "Cancel"}</CoAppTextButton>
				<CoAppActionButton onClick={isConfirmationStep ? handleConfirmClick : handleClickNext}>{isConfirmationStep ? "Save" : "Next"}</CoAppActionButton>
			</DialogActions>
		</CoAppDialog >
	);
}